/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  FormEvent,
  createElement,
  useEffect,
  useSyncExternalStore,
} from 'react'
import { get } from 'lodash'

// ---- Types ----

export type Validator<T = any> = {
  validate: (
    data: FormData | Record<string, unknown>,
  ) => Promise<{ data?: T; error?: { fieldErrors: Record<string, string> } }>
}

type ValidationResult<T> = { data?: T; error?: { fieldErrors: Record<string, string> } }

// A missing validator is a wiring bug, not a mode. `ValidatedForm` types
// `validator` as required and every caller supplies a `withZod(...)` schema.
// Passing the raw form values through as "valid" would skip zod's parsing and
// coercion (numbers, arrays, nested objects) and hand the consumer a mis-shaped
// object. Asserting here names the fault instead of the cryptic
// `reading 'validate'` that a bare `validator.validate(...)` produced, and it
// runs synchronously (before the submit's async work) so the throw is a normal
// handler error, not an unhandled rejection (TRK-6070).
function assertValidator<T>(
  validator: Validator<T> | undefined,
  formId: string | undefined,
): asserts validator is Validator<T> {
  if (!validator) {
    throw new Error(
      `ValidatedForm${formId ? ` "${formId}"` : ``} was rendered without a validator. ` +
        `Pass a withZod(...) validator so the form can validate on submit.`,
    )
  }
}

export type FormProps<
  DataType extends Record<string, unknown> = Record<string, unknown>,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _SubmitResponseData = unknown,
> = {
  validator: Validator<DataType>
  onSubmit?: (data: DataType, event: FormEvent<HTMLFormElement>) => void | Promise<void>
  children?: React.ReactNode
  method?: string
  action?: string
  id?: string
  className?: string
  preventSubmit?: boolean
  defaultValues?: Partial<DataType>
  subaction?: string
  encType?: string
  replace?: boolean
  fetcher?: unknown
  autoComplete?: string
  formRef?: React.RefObject<HTMLFormElement | null>
  onChange?: (event: FormEvent<HTMLFormElement>) => void
  [key: string]: unknown
}

export type TouchedFields = Record<string, boolean>

// ---- Context ----

type FormContextValue = {
  fieldErrors: Record<string, string>
  isSubmitting: boolean
  touchedFields: Record<string, boolean>
  values: Record<string, unknown>
  controlledValues: Record<string, unknown>
  setControlledValue: (name: string, value: unknown) => void
  setFieldValue: (name: string, value: unknown) => void
  setFieldTouched: (name: string, touched: boolean) => void
  validate: () => Promise<boolean>
  reset: () => void
  submit: () => void
  getValues: () => Map<string, string>
  formRef: React.RefObject<HTMLFormElement | null>
  validator: Validator
  defaultValues: Record<string, unknown>
}

const FormContext = createContext<FormContextValue | null>(null)

// Registry so hooks can look up a form by id. Hooks called ABOVE the provider
// (a component that renders its own SmartForm, then reads the form by id) get
// nothing from React context, so the registry is their only route in. It has to
// be subscribable or those hooks never re-render when the form's state moves.
const formRegistry = new Map<string, FormContextValue>()
const formListeners = new Map<string, Set<() => void>>()

const notifyForm = (id: string) => formListeners.get(id)?.forEach((listener) => listener())

function subscribeToForm(id: string, listener: () => void) {
  const listeners = formListeners.get(id) ?? new Set<() => void>()
  formListeners.set(id, listeners)
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
    if (!listeners.size) formListeners.delete(id)
  }
}

// ---- ValidatedForm ----

export function ValidatedForm<DataType extends Record<string, unknown>>({
  validator,
  onSubmit,
  children,
  className,
  id,
  formRef: externalRef,
  defaultValues = {},
  onChange,
  // consumed but not forwarded
  preventSubmit: _preventSubmit,
  subaction: _subaction,
  replace: _replace,
  fetcher: _fetcher,
  ...rest
}: FormProps<DataType>) {
  const internalRef = useRef<HTMLFormElement>(null)
  const formRef = (externalRef ?? internalRef) as React.RefObject<HTMLFormElement | null>

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [touchedFields, setTouchedFields] = useState<Record<string, boolean>>({})
  const [controlledValues, setControlledValues] = useState<Record<string, unknown>>({})

  const setControlledValue = useCallback((name: string, value: unknown) => {
    setControlledValues((prev) => ({ ...prev, [name]: value }))
  }, [])

  const setFieldValue = useCallback((name: string, value: unknown) => {
    setControlledValues((prev) => ({ ...prev, [name]: value }))
  }, [])

  const setFieldTouched = useCallback((name: string, touched: boolean) => {
    setTouchedFields((prev) => ({ ...prev, [name]: touched }))
  }, [])

  const getValues = useCallback((): Map<string, string> => {
    const map = new Map<string, string>()
    if (formRef.current) {
      const fd = new FormData(formRef.current)
      fd.forEach((v, k) => map.set(k, String(v)))
    }
    // overlay controlled values
    Object.entries(controlledValues).forEach(([k, v]) => {
      if (v !== null && v !== undefined && typeof v === 'object') {
        map.set(k, JSON.stringify(v))
      } else {
        map.set(k, String(v ?? ''))
      }
    })
    return map
  }, [formRef, controlledValues])

  const validate = useCallback(async (): Promise<boolean> => {
    assertValidator(validator, id)
    const fd = formRef.current ? new FormData(formRef.current) : new FormData()
    const result: ValidationResult<DataType> = await validator.validate(fd)
    if (result.error) {
      setFieldErrors(result.error.fieldErrors)
      return false
    }
    setFieldErrors({})
    return true
  }, [validator, formRef])

  const reset = useCallback(() => {
    formRef.current?.reset()
    setFieldErrors({})
    setTouchedFields({})
    setControlledValues({})
  }, [formRef])

  const submit = useCallback(() => {
    formRef.current?.requestSubmit()
  }, [formRef])

  const ctx: FormContextValue = {
    fieldErrors,
    isSubmitting,
    touchedFields,
    values: controlledValues,
    controlledValues,
    setControlledValue,
    setFieldValue,
    setFieldTouched,
    validate,
    reset,
    submit,
    getValues,
    formRef,
    validator,
    defaultValues: defaultValues as Record<string, unknown>,
  }

  // Register by id so useFormContext(id) can find it
  useEffect(() => {
    if (id) formRegistry.set(id, ctx)
    return () => {
      if (id) formRegistry.delete(id)
    }
  })

  // Wake the hooks reading this form through the registry. Keyed on everything
  // they can observe, defaultValues included: a field array seeded from them
  // would otherwise hold the previous form's rows when the caller passes new
  // ones. Listeners compare snapshots, so a re-render that changes nothing they
  // read does not loop back through them.
  useEffect(() => {
    if (id) notifyForm(id)
  }, [id, controlledValues, fieldErrors, touchedFields, isSubmitting, defaultValues])

  const runSubmit = async (e: FormEvent<HTMLFormElement>) => {
    setIsSubmitting(true)
    try {
      const fd = new FormData(e.currentTarget)
      const result: ValidationResult<DataType> = await validator!.validate(fd)
      if (result.error) {
        setFieldErrors(result.error.fieldErrors)
        return
      }
      setFieldErrors({})
      await onSubmit?.(result.data as DataType, e)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Sync wrapper: the validator assertion must throw synchronously so a wiring
  // bug is a normal handler error (caught by an error boundary / window.onerror),
  // not an unhandled rejection from an async handler. The async submit's own
  // failures are logged rather than left floating.
  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    assertValidator(validator, id)
    runSubmit(e).catch((error) => {
      // eslint-disable-next-line no-console -- surface a submit failure; the form has no error channel here
      console.error(`ValidatedForm${id ? ` "${id}"` : ``} submit failed`, error)
    })
  }

  return createElement(
    FormContext.Provider,
    { value: ctx },
    createElement(
      'form',
      {
        ref: formRef,
        id,
        className,
        onSubmit: handleSubmit,
        onChange,
        ...rest,
      },
      children,
    ),
  )
}

// ---- useField ----

type UseFieldOptions = {
  formId?: string
  validationBehavior?: unknown
}

type UseFieldReturn = {
  error: string | undefined
  touched: boolean
  defaultValue: unknown
  setTouched: (touched: boolean) => void
  clearError: () => void
  validate: () => Promise<void>
  getInputProps: (props?: Record<string, unknown>) => Record<string, unknown>
}

export function useField(name: string, options?: UseFieldOptions): UseFieldReturn {
  const ctxPublic = useFormContext(options?.formId)
  const ctxInternal = useContext(FormContext)
  const source = options?.formId ? (formRegistry.get(options.formId) ?? ctxInternal) : ctxInternal

  const error = ctxPublic.fieldErrors[name]
  const touched = ctxPublic.touchedFields[name] ?? false
  // `name` is a path for array fields (`patients[0].outcome`), so it has to be
  // walked rather than used as a flat key, or rows never show their defaults.
  const defaultValue = get(source?.defaultValues, name)

  const setTouched = useCallback((t: boolean) => ctxPublic.setFieldTouched(name, t), [ctxPublic, name])

  const clearError = useCallback(() => {
    // no-op: errors clear on next submit
  }, [])

  const validate = useCallback(async () => {
    await ctxPublic.validate()
  }, [ctxPublic])

  const getInputProps = useCallback(
    (extra: Record<string, unknown> = {}): Record<string, unknown> => {
      const { onChange: extraOnChange, onBlur: extraOnBlur, ...rest } = extra
      return {
        name,
        id: name,
        defaultValue,
        onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
          if (typeof extraOnChange === 'function') extraOnChange(e)
        },
        onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
          setTouched(true)
          if (typeof extraOnBlur === 'function') extraOnBlur(e)
        },
        ...rest,
      }
    },
    [name, defaultValue, setTouched],
  )

  return { error, touched, defaultValue, setTouched, clearError, validate, getInputProps }
}

// ---- useFieldArray ----

type FieldArrayHelpers<T> = {
  push: (item: T) => void
  remove: (index: number) => void
  replace: (index: number, item: T) => void
  swap: (indexA: number, indexB: number) => void
  move: (from: number, to: number) => void
  insert: (index: number, item: T) => void
  unshift: (item: T) => void
  pop: () => void
}

export function useFieldArray<T = Record<string, unknown>>(
  name: string,
  options?: { formId?: string },
): [T[], FieldArrayHelpers<T>] {
  const ctxInternal = useContext(FormContext)
  const formId = options?.formId
  const key = `__fieldArray__${name}`

  // Resolved lazily rather than captured at render: the registry entry is written
  // in an effect, so on the first render it does not exist yet and a helper that
  // closed over it would silently do nothing.
  const getSource = useCallback(
    () => (formId ? (formRegistry.get(formId) ?? ctxInternal) : ctxInternal),
    [formId, ctxInternal],
  )

  // The snapshot is the stored array itself, so it stays referentially stable
  // between renders and only wakes this component when the array really changed.
  const getSnapshot = useCallback(() => {
    const source = getSource()
    return source?.controlledValues[key] ?? get(source?.defaultValues, name)
  }, [getSource, key, name])

  const subscribe = useCallback(
    (listener: () => void) => (formId ? subscribeToForm(formId, listener) : () => {}),
    [formId],
  )

  const raw = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
  const items: T[] = Array.isArray(raw) ? (raw as T[]) : []

  const set = useCallback((next: T[]) => getSource()?.setControlledValue(key, next), [getSource, key])

  const helpers: FieldArrayHelpers<T> = {
    push: (item) => set([...items, item]),
    remove: (i) => set(items.filter((_, idx) => idx !== i)),
    replace: (i, item) => set(items.map((v, idx) => (idx === i ? item : v))),
    swap: (a, b) => {
      const next = [...items]
      ;[next[a], next[b]] = [next[b], next[a]]
      set(next)
    },
    move: (from, to) => {
      const next = [...items]
      const [item] = next.splice(from, 1)
      next.splice(to, 0, item)
      set(next)
    },
    insert: (i, item) => {
      const next = [...items]
      next.splice(i, 0, item)
      set(next)
    },
    unshift: (item) => set([item, ...items]),
    pop: () => set(items.slice(0, -1)),
  }

  return [items, helpers]
}

// ---- useFormContext ----

type FormContextPublic = {
  fieldErrors: Record<string, string>
  isSubmitting: boolean
  touchedFields: Record<string, boolean>
  validate: () => Promise<boolean>
  reset: () => void
  submit: () => void
  getValues: () => Map<string, string>
  setFieldValue: (name: string, value: unknown) => void
  setFieldTouched: (name: string, touched: boolean) => void
  action?: string
  isValid: boolean
}

export function useFormContext(formId?: string): FormContextPublic {
  const ctx = useContext(FormContext)

  if (formId) {
    const registered = formRegistry.get(formId)
    if (registered)
      return { ...registered, action: undefined, isValid: Object.keys(registered.fieldErrors).length === 0 }
  }

  if (!ctx) {
    // Return a safe no-op context so components outside a form don't crash
    return {
      fieldErrors: {},
      isSubmitting: false,
      touchedFields: {},
      validate: async () => true,
      reset: () => {},
      submit: () => {},
      getValues: () => new Map(),
      setFieldValue: () => {},
      setFieldTouched: () => {},
      action: undefined,
      isValid: true,
    }
  }

  return { ...ctx, action: undefined, isValid: Object.keys(ctx.fieldErrors).length === 0 }
}

// ---- useIsSubmitting ----

export function useIsSubmitting(formId?: string): boolean {
  const ctx = useFormContext(formId)
  return ctx.isSubmitting
}

// ---- useControlField ----

export function useControlField<T = string>(name: string, formId?: string): [T, (value: T) => void] {
  const ctx = useFormContext(formId)
  const raw = useContext(FormContext)

  // Prefer the nearest context's controlled value
  const source = formId ? formRegistry.get(formId) : raw
  const value = (source?.controlledValues[name] ?? get(source?.defaultValues, name) ?? '') as T

  const setValue = useCallback(
    (v: T) => {
      if (source) {
        source.setControlledValue(name, v)
      } else {
        ctx.setFieldValue(name, v)
      }
    },
    [source, ctx, name],
  )

  return [value, setValue]
}
